import React from 'react';
import { ReactSpreadsheetImport } from 'react-spreadsheet-import';
import { translations } from '../import/ExcelImporter';
import { inventoryCountService, TheoreticalSourceType } from '../../../../core/modules/inventoryCount/services/api';

interface Props {
  isOpen: boolean;
  companyId: number;
  sessionId: number;
  // 'operator' = teorico propio; 'client' = teorico del dueno de la carga
  // (caso 3PL, ej. AGUNSA) -- se elige ANTES de abrir el dialogo, con un
  // boton separado por cada uno (ver InventoryCountAdminPage).
  sourceType: TheoreticalSourceType;
  onClose: () => void;
  onImported: (result: { loadedCount: number; createdProductsCount: number }) => void;
}

const fields = [
  {
    label: 'SKU',
    key: 'sku',
    fieldType: { type: 'input' as const },
    alternateMatches: ['sku', 'SKU', 'codigo', 'código', 'code'],
    validations: [{ rule: 'required' as const, errorMessage: 'El SKU es requerido.' }],
  },
  {
    label: 'Producto',
    key: 'productName',
    fieldType: { type: 'input' as const },
    alternateMatches: ['producto', 'descripcion', 'descripción', 'nombre', 'product_name'],
  },
  {
    label: 'Cantidad teórica',
    key: 'theoreticalQty',
    fieldType: { type: 'input' as const },
    alternateMatches: ['cantidad', 'cantidad teorica', 'cantidad teórica', 'stock', 'quantity', 'theoretical_qty'],
    validations: [
      { rule: 'required' as const, errorMessage: 'La cantidad teórica es requerida.' },
      { rule: 'regex' as const, value: '^[0-9]+(\\.[0-9]+)?$', errorMessage: 'Debe ser un número válido (0 o mayor).' },
    ],
  },
  {
    label: 'Ubicación',
    key: 'location',
    fieldType: { type: 'input' as const },
    alternateMatches: ['ubicacion', 'ubicación', 'zona', 'pasillo', 'location'],
  },
];

const TheoreticalUploadDialog = ({ isOpen, companyId, sessionId, sourceType, onClose, onImported }: Props) => {
  const handleSubmit = async (data: { validData: any[] }) => {
    if (!Array.isArray(data.validData) || data.validData.length === 0) {
      alert('El archivo no tiene filas válidas para importar.');
      onClose();
      return;
    }
    try {
      const items = data.validData.map((row) => ({
        sku: String(row.sku ?? '').trim(),
        productName: row.productName ? String(row.productName).trim() : undefined,
        theoreticalQty: Number(row.theoreticalQty),
        location: row.location ? String(row.location).trim() : undefined,
      }));
      const result = await inventoryCountService.loadTheoreticalUpload(companyId, sessionId, items, sourceType);
      const sourceLabel = sourceType === 'client' ? 'teórico del cliente' : 'teórico del operador';
      alert(
        `Se cargó el ${sourceLabel}: ${result.loadedCount} SKUs` +
          (result.createdProductsCount > 0 ? ` (${result.createdProductsCount} productos nuevos creados).` : '.')
      );
      onImported(result);
    } catch (err) {
      alert('No se pudo cargar el archivo. Revisa los datos e intenta de nuevo.');
    } finally {
      onClose();
    }
  };

  return (
    <ReactSpreadsheetImport
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      fields={fields as any}
      translations={translations}
      customTheme={{
        components: {
          UploadStep: {
            baseStyle: {
              heading: { fontSize: '3xl', color: 'textColor', mb: '2rem' },
              title: { fontSize: '3xl', lineHeight: 8, fontWeight: 'semibold', color: 'textColor' },
              subtitle: { lineHeight: 8, color: 'subtitleColor', mb: '1rem' },
              tableWrapper: { mb: '0.5rem', position: 'relative', h: '100px' },
              dropzoneText: { size: 'xl', lineHeight: 7, fontWeight: 'semibold', color: 'textColor' },
              dropZoneBorder: 'rsi.500',
              dropzoneButton: { mt: '1rem' },
            },
          },
          Modal: {
            baseStyle: {
              dialog: { borderRadius: 'lg', fontSize: '3xl', color: 'textColor' },
              closeModalButton: {},
              backButton: { gridColumn: '1', gridRow: '1', justifySelf: 'start' },
              continueButton: { gridColumn: '1 / 3', gridRow: '1', justifySelf: 'center' },
            },
            variants: {
              rsi: {
                body: {
                  bg: 'background',
                  display: 'flex',
                  paddingX: '2rem',
                  paddingY: '2rem',
                  flexDirection: 'column',
                  flex: 1,
                  overflow: 'auto',
                  height: '100%',
                },
                footer: {
                  bg: 'secondaryBackground',
                  py: '1.5rem',
                  px: '2rem',
                  justifyContent: 'center',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gridTemplateRows: '1fr',
                  gap: '1rem',
                },
                dialog: {
                  outline: 'unset',
                  minH: 'calc(var(--chakra-vh) - 4rem)',
                  maxW: 'calc(var(--chakra-vw) - 4rem)',
                  my: '2rem',
                  borderRadius: '3xl',
                  overflow: 'hidden',
                },
              },
            },
          },
        },
      }}
    />
  );
};

export default TheoreticalUploadDialog;
